import { Editor, JSONContent } from '@tiptap/core';
import { InternalLinkFormat } from '../../tiptap/extensions/plugins/InternalLink';
import { htmlIsEmpty } from '../../../../components/richText';

export interface IeeeCitationCandidate {
  headingId: string;
  proposedText: string;
  occurrenceCount: number;
}

const MARK_TAGS: Record<string, string> = { bold: 'strong', italic: 'em', underline: 'u' };

const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
const escapeHtml = (text: string): string => text.replace(/[&<>"]/g, (c) => ESCAPES[c]);

// Keeps only the marks RichTextField supports (see components/richText/config.ts).
// Unsupported marks (highlight, sub/superscript, attachment, comments) are dropped, their text kept.
const extractHtml = (node: JSONContent): string => {
  if (node.type === 'text') {
    return (node.marks ?? []).reduce(
      (html, mark) => {
        const tag = MARK_TAGS[mark.type];
        if (tag) return `<${tag}>${html}</${tag}>`;
        if (mark.type === 'link' && mark.attrs?.href) {
          return `<a target="_blank" rel="noopener noreferrer" href="${escapeHtml(mark.attrs.href)}">${html}</a>`;
        }
        return html;
      },
      escapeHtml(node.text ?? ''),
    );
  }
  if (node.type === 'hardBreak') return '<br>';
  return (node.content ?? []).map(extractHtml).join('');
};

const collectHeadingsById = (root: JSONContent): Map<string, JSONContent> => {
  const map = new Map<string, JSONContent>();
  const walk = (node: JSONContent) => {
    if (node.type === 'heading' && node.attrs?.id) {
      map.set(node.attrs.id as string, node);
    }
    (node.content ?? []).forEach(walk);
  };
  walk(root);
  return map;
};

export const findIeeeCitationCandidates = (editor: Editor): IeeeCitationCandidate[] => {
  const root = editor.getJSON();
  const headingsById = collectHeadingsById(root);
  const occurrences = new Map<string, number>();

  const walk = (node: JSONContent) => {
    if (node.type === 'internalLink' && node.attrs?.format === InternalLinkFormat.CitationIEEEStyle && node.attrs?.id) {
      const headingId = node.attrs.id as string;
      occurrences.set(headingId, (occurrences.get(headingId) ?? 0) + 1);
    }
    (node.content ?? []).forEach(walk);
  };
  walk(root);

  const candidates: IeeeCitationCandidate[] = [];
  for (const [headingId, occurrenceCount] of occurrences) {
    const heading = headingsById.get(headingId);
    const inner = heading ? extractHtml(heading) : '';
    const proposedText = htmlIsEmpty(inner) ? '<p>(uten tittel)</p>' : `<p>${inner}</p>`;
    candidates.push({ headingId, proposedText, occurrenceCount });
  }

  return candidates;
};

export interface MigrationSelection {
  headingId: string;
  citationId: string;
  text: string;
}

export const replaceIeeeLinksInDoc = (editor: Editor, selections: MigrationSelection[]): void => {
  const byHeading = new Map(selections.map((s) => [s.headingId, s.citationId]));
  const replacements: { from: number; to: number; citationId: string }[] = [];

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'internalLink') return;
    if (node.attrs.format !== InternalLinkFormat.CitationIEEEStyle) return;
    const citationId = byHeading.get(node.attrs.id);
    if (!citationId) return;
    replacements.push({ from: pos, to: pos + node.nodeSize, citationId });
  });

  if (replacements.length === 0) return;

  const { tr, schema } = editor.state;
  for (let i = replacements.length - 1; i >= 0; i--) {
    const { from, to, citationId } = replacements[i];
    tr.replaceWith(from, to, schema.nodes.citationLink.create({ citationId }));
  }
  editor.view.dispatch(tr);
};
